import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

interface Client {
  id: number
  name: string
  email: string
}

interface Gallery {
  id: number
  name: string
  description: string
  tier: 'proof' | 'preview' | 'delivery'
  image_count: number
  preview_images: string[]
  created_at: string
}

interface ImageVariant {
  label: string
  width: number
  height: number
  format: string
  url: string
}

interface GalleryImage {
  id: number
  title: string
  alt: string
  ratio: number | null
  is_favorite: boolean
  src: string
  thumb: string
  variants: ImageVariant[]
}

const TOKEN_KEY = 'clientToken'

export const useClientStore = defineStore('client', () => {
  const client = ref<Client | null>(null)
  const galleries = ref<Gallery[]>([])
  const currentGallery = ref<Gallery | null>(null)
  const currentGalleryImages = ref<GalleryImage[]>([])
  const isLoading = ref(false)

  const isAuthenticated = computed(() => client.value !== null)

  // All calls go through /api — CloudFront rewrites /api/* → API Gateway
  const token = () => localStorage.getItem(TOKEN_KEY) ?? ''

  const apiFetch = async (path: string, options: RequestInit = {}): Promise<Response> => {
    const res = await fetch(`/api${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token()}`,
        ...(options.headers ?? {}),
      },
    })
    return res
  }

  const login = async (email: string, password: string) => {
    isLoading.value = true
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Login failed')
      }

      // Store token from response body (Lambda returns { token, client })
      if (data.token) {
        localStorage.setItem(TOKEN_KEY, data.token)
      }

      client.value = data.client
      return data.client
    } finally {
      isLoading.value = false
    }
  }

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY)
    client.value = null
    galleries.value = []
    currentGallery.value = null
    currentGalleryImages.value = []
  }

  // Request a password-reset email. Always resolves (the API returns a generic
  // success whether or not the email is registered), so the UI shows the same
  // confirmation either way.
  const requestPasswordReset = async (email: string): Promise<string> => {
    const response = await fetch('/api/auth/forgot-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    })
    const data = await response.json().catch(() => ({}))
    return data.message || 'If that email is registered, a reset link is on its way.'
  }

  // Complete a reset using the emailed token + a new password.
  const resetPassword = async (token: string, password: string): Promise<string> => {
    const response = await fetch('/api/auth/reset-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, password }),
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(data.error || 'Could not reset password.')
    return data.message || 'Your password has been reset.'
  }

  const verifyAuth = async (): Promise<boolean> => {
    const t = token()
    if (!t) return false
    try {
      const response = await apiFetch('/auth/verify')
      if (response.ok) {
        const data = await response.json()
        client.value = data.client
        return true
      }
    } catch {
      // network error — stay unauthenticated
    }
    localStorage.removeItem(TOKEN_KEY)
    return false
  }

  const fetchGalleries = async () => {
    isLoading.value = true
    try {
      const response = await apiFetch('/galleries')
      if (!response.ok) throw new Error('Failed to fetch galleries')
      const data = await response.json()
      galleries.value = data.galleries
    } finally {
      isLoading.value = false
    }
  }

  const fetchGalleryImages = async (galleryId: number) => {
    isLoading.value = true
    try {
      const response = await apiFetch(`/galleries/${galleryId}`)
      if (!response.ok) throw new Error('Failed to fetch gallery images')
      const data = await response.json()
      currentGalleryImages.value = data.images
      // Prefer gallery from the already-loaded list, but if navigating directly
      // the list may be empty — fall back to the tier returned by the Lambda.
      const fromList = galleries.value.find(g => g.id === galleryId)
      if (fromList) {
        currentGallery.value = fromList
      } else if (data.tier) {
        currentGallery.value = {
          id: galleryId,
          name: '',
          description: '',
          tier: data.tier,
          image_count: data.images.length,
          preview_images: [],
          created_at: '',
        }
      } else {
        currentGallery.value = null
      }
      return data.images
    } finally {
      isLoading.value = false
    }
  }

  const toggleFavorite = async (galleryId: number, imageId: number) => {
    const response = await apiFetch('/favorites', {
      method: 'POST',
      body: JSON.stringify({ galleryId, imageId }),
    })
    if (!response.ok) throw new Error('Failed to toggle favorite')
    const data = await response.json()

    // Update local state optimistically
    const imageIndex = currentGalleryImages.value.findIndex(img => img.id === imageId)
    if (imageIndex !== -1) {
      currentGalleryImages.value[imageIndex].is_favorite = data.isFavorite
    }

    return data.isFavorite
  }

  const addComment = async (galleryId: number, imageId: number, body: string) => {
    const response = await apiFetch('/comments', {
      method: 'POST',
      body: JSON.stringify({ galleryId, imageId, body }),
    })
    if (!response.ok) throw new Error('Failed to add comment')
    return (await response.json()).comment
  }

  const fetchComments = async (galleryId: number, imageId: number) => {
    const response = await apiFetch(`/comments?galleryId=${galleryId}&imageId=${imageId}`)
    if (!response.ok) throw new Error('Failed to load comments')
    return (await response.json()).comments ?? []
  }

  const updateComment = async (commentId: number, body: string) => {
    const response = await apiFetch(`/comments/${commentId}`, {
      method: 'PATCH',
      body: JSON.stringify({ body }),
    })
    if (!response.ok) throw new Error('Failed to update comment')
  }

  const deleteComment = async (commentId: number) => {
    const response = await apiFetch(`/comments/${commentId}`, { method: 'DELETE' })
    if (!response.ok) throw new Error('Failed to delete comment')
  }

  const requestDownload = async (galleryId: number) => {
    const response = await apiFetch(`/galleries/${galleryId}/download`)
    if (!response.ok) {
      const data = await response.json()
      throw new Error(data.error || 'Download not available')
    }
    return (await response.json()).downloadUrl as string
  }

  return {
    client,
    galleries,
    currentGallery,
    currentGalleryImages,
    isLoading,
    isAuthenticated,
    login,
    logout,
    requestPasswordReset,
    resetPassword,
    verifyAuth,
    fetchGalleries,
    fetchGalleryImages,
    toggleFavorite,
    addComment,
    fetchComments,
    updateComment,
    deleteComment,
    requestDownload,
  }
})
